import { defaultTo, get } from 'lodash';
import { useMemo } from 'react';
import i18n from 'i18n';
import { FormConfig } from 'components/common/form/FormConfig';
import { useFormikContext } from 'formik';
import {
  ChannelGroupFormData,
  ChannelType,
  NameI18n
} from 'core/channelGroup/ChannelGroup';
import { FormContent } from 'components/common/form/Form';
import { ChannelGroupSetting } from 'core/channelGroup/ChannelGroupSettings';
import styles from './channelGroupBasicForm.module.scss';
import { validateEmail, validateEmpty } from 'utils/ValidateUtils';
import {
  BRAND_ICON_VALID_TYPES,
  validateBrandIcon,
  validateBrandName,
  validateBrandUrl
} from './channelGroupBrandValidator';
import {
  getCompositeCreativeError,
  getCompositeCreativeText
} from 'core/channelGroup/compositeSettingDisplay';

// alertEmails 可以留空，但填入的每一筆都必須是合法 email
function validateAlertEmails(emails: string[] | undefined) {
  const hasInvalidEmail = defaultTo(emails, []).some(
    (email) => validateEmail(email) !== undefined
  );
  return hasInvalidEmail
    ? i18n.t('formValidate.labels.invalidEmail')
    : undefined;
}

const BRAND_NAME_FIELDS: { language: keyof NameI18n; labelKey: string }[] = [
  { language: 'ja', labelKey: 'channelGroupForm.labels.brandNameI18n.ja' },
  { language: 'en', labelKey: 'channelGroupForm.labels.brandNameI18n.en' },
  { language: 'zh-TW', labelKey: 'channelGroupForm.labels.brandNameI18n.zh-tw' }
];

// 地圖選門市時用到的品牌名稱與 pin 圖示，全部選填
function buildBrandFields() {
  const fieldsBuilder = new FormConfig.FieldsBuilder();
  BRAND_NAME_FIELDS.forEach(({ language, labelKey }) => {
    fieldsBuilder.addFormikInput({
      label: i18n.t(labelKey),
      name: `property.addonProps.brand.nameI18n.${language}`,
      validate: validateBrandName
    });
  });
  fieldsBuilder.addFormikInput({
    label: i18n.t('channelGroupForm.labels.brandUrl'),
    name: 'property.addonProps.brand.url',
    validate: validateBrandUrl
  });
  const iconHints = [
    i18n.t('channelGroupForm.labels.brandIconTypeHint'),
    i18n.t('channelGroupForm.labels.brandIconPinHint'),
    i18n.t('creativeSetupFlow.labels.storageHint', { storage: '200KB' })
  ];
  fieldsBuilder.addFormikFileInput({
    type: 'image',
    label: i18n.t('channelGroupForm.labels.brandFilteredIcon'),
    name: 'brandFilteredIconData',
    validTypes: BRAND_ICON_VALID_TYPES,
    hints: iconHints,
    validate: validateBrandIcon
  });
  fieldsBuilder.addFormikFileInput({
    type: 'image',
    label: i18n.t('channelGroupForm.labels.brandNormalIcon'),
    name: 'brandNormalIconData',
    validTypes: BRAND_ICON_VALID_TYPES,
    hints: iconHints,
    validate: validateBrandIcon
  });
  return fieldsBuilder.build();
}

export const ChannelGroupBasicForm: React.FC<{
  channelGroupId: number;
}> = ({ channelGroupId }) => {
  const { values } = useFormikContext<ChannelGroupFormData>();
  const limitPublisherId = get(
    values,
    'addonFeatures.campaign.limitPublisherId',
    true
  );
  const {
    isScreenSpliced,
    isMultiScreenSameVideo,
    screenSplicedVerticalScreenCount,
    screenSplicedHorizontalScreenCount
  } = values.screenProperty || {
    isScreenSpliced: false,
    isMultiScreenSameVideo: false,
    screenSplicedVerticalScreenCount: 0,
    screenSplicedHorizontalScreenCount: 0
  };
  // ECOPD-5722: 合成設定另外取，不要併進上面那個 fallback 物件——
  // isCompositeCreative 是三態，補一個 `false` 進去就分不出「RMax 還沒同步」了。
  const compositeScreenProperty = values.screenProperty;
  const isCompositeCreative = compositeScreenProperty?.isCompositeCreative;
  const compositeSettingStatus =
    compositeScreenProperty?.compositeSettingStatus;
  const publisherIds = get(values, 'publisherIds', []);
  const isBeaconChannel = values.channelType === ChannelType.BEACON;
  const isEpaperChannel = values.channelType === ChannelType.E_PAPER;
  // ECOPD-5581: E_PAPER 刻意不列入——電子紙只投圖片（沒有喇叭、supportAudio 恆為
  // false），「支援的影片編碼」對它沒有意義；而且該列在 allowedVideoCodecs 為空時
  // 會顯示「所有」，對一個根本不支援影片的渠道等於講反話。
  const isOutdoorChannel = [
    ChannelType.OUTDOOR,
    ChannelType.MOVABLE_OUTDOOR
  ].includes(values.channelType);
  // ECOPD-5629: static 渠道的 addonFeatures 在 runtime registry 恆為 {}，這支旗標對
  // 它們讀不到、切了也沒有效果；其中只有 RTB / FB 內建 CBO。
  const isStaticChannel =
    ChannelGroupSetting.isStaticChannelGroup(channelGroupId);
  const showBrandSection = ChannelGroupSetting.isOutdoorChannelType(
    values.channelType
  );
  const isCboBuiltInChannel =
    ChannelGroupSetting.isCboBuiltInChannelGroup(channelGroupId);

  const basicFormConfig = useMemo(() => {
    const fieldsBuilder = new FormConfig.FieldsBuilder();
    fieldsBuilder.addFormikLabel({
      label: i18n.t('channelGroupForm.labels.name'),
      name: 'name'
    });

    fieldsBuilder.addFormikInput({
      label: i18n.t('channelGroupForm.labels.nameI18n.ja'),
      name: 'nameI18n.ja',
      validate: validateEmpty
    });

    fieldsBuilder.addFormikInput({
      label: i18n.t('channelGroupForm.labels.nameI18n.en'),
      name: 'nameI18n.en',
      validate: validateEmpty
    });

    fieldsBuilder.addFormikInput({
      label: i18n.t('channelGroupForm.labels.nameI18n.zh-tw'),
      name: 'nameI18n.zh-TW',
      validate: validateEmpty
    });

    fieldsBuilder.addFormikTags({
      label: i18n.t('channelGroupForm.labels.alertEmails'),
      name: 'alertEmails',
      placeholder: i18n.t('channelGroupForm.placeholders.alertEmails'),
      validate: validateAlertEmails
    });

    fieldsBuilder.addFormikSwitch({
      label: i18n.t('channelGroupForm.labels.limitPublisherId'),
      name: 'addonFeatures.campaign.limitPublisherId',
      disabled: true
    });

    const publisherIdPlaceholder = limitPublisherId
      ? i18n.t('common.labels.noData')
      : i18n.t('channelGroupForm.labels.publisherNotLimited');

    fieldsBuilder.addLabel({
      label: i18n.t('channelGroupForm.labels.publisherIds'),
      value:
        publisherIds.length > 0
          ? publisherIds.join(', ')
          : publisherIdPlaceholder,
      hint:
        limitPublisherId && publisherIds.length === 0 ? (
          <span className={styles.warning}>
            {i18n.t('channelGroupForm.labels.noPublisherIdWarning')}
          </span>
        ) : undefined
    });

    values.deliveryUnit &&
      fieldsBuilder.addLabel({
        label: i18n.t('channelGroup.labels.deliveryUnit'),
        value: i18n.t(`deliveryUnit.${values.deliveryUnit.toLowerCase()}`)
      });

    // ECOPD-5581: 元太系統的 site 識別碼，只有電子紙渠道有。值由 RMax 同步寫入，
    // PMax 這側唯讀——所以用 addLabel 而不是 addFormikInput，畫面上沒有可編輯的入口。
    // （送出時另由 wrapForServer 把 siteId 從 payload omit 掉，兩道一起才擋得住。）
    isEpaperChannel &&
      fieldsBuilder.addLabel({
        label: i18n.t('channelGroup.labels.siteId'),
        value:
          values.property?.epaperProps?.siteId || i18n.t('common.labels.noData')
      });

    // ECOPD-5470: 投遞渠道 tab 顯示該渠道允許的影片編碼(唯讀)。
    // 僅影片型 outdoor(OUTDOOR/MOVABLE_OUTDOOR)顯示;Beacon/RTB 恆為 []（RMax 定義)不出列。
    // allowedVideoCodecs 為空＝不限制 → 顯示「所有」。
    isOutdoorChannel &&
      fieldsBuilder.addLabel({
        label: i18n.t('channelGroup.labels.allowedVideoCodecs'),
        value: values.allowedVideoCodecs?.length
          ? values.allowedVideoCodecs.join(', ')
          : i18n.t('channelGroup.labels.allowedVideoCodecsAll')
      });

    fieldsBuilder.addSwitch({
      label: i18n.t('channelGroup.labels.isScreenSpliced'),
      name: 'isScreenSpliced',
      value: isScreenSpliced,
      disabled: true
    });

    const verticalScreenUnit =
      screenSplicedVerticalScreenCount > 1
        ? i18n.t('common.units.devices')
        : i18n.t('common.units.device');
    const horizontalScreenUnit =
      screenSplicedHorizontalScreenCount > 1
        ? i18n.t('common.units.devices')
        : i18n.t('common.units.device');
    if (isScreenSpliced) {
      fieldsBuilder
        .addLabel({
          label: i18n.t('channelGroup.labels.screenSplicedVerticalScreenCount'),
          value: `${screenSplicedVerticalScreenCount} ${verticalScreenUnit}`
        })
        .addLabel({
          label: i18n.t(
            'channelGroup.labels.screenSplicedHorizontalScreenCount'
          ),
          value: `${screenSplicedHorizontalScreenCount} ${horizontalScreenUnit}`
        });
    }

    // 這兩列的出現條件要跟後端輸出 compositeSettingStatus 的條件一樣，兩種都算：
    //   1. 連台渠道
    //   2. 連台已經關掉，但底下還留著連台素材類型
    // 少了第 2 種，那種渠道的素材會被擋下來，系統管理員卻在這一頁看不到任何異常。
    if (isScreenSpliced || compositeSettingStatus !== undefined) {
      const notSyncedError = getCompositeCreativeError(compositeSettingStatus);
      // RMax 還沒同步時沒有值可以放進開關，只有這一種情況降級成文字；
      // 有值就跟上下兩列的開關長得一樣。
      if (isCompositeCreative === undefined) {
        fieldsBuilder.addLabel({
          label: i18n.t('channelGroup.labels.isCompositeCreative'),
          value: getCompositeCreativeText(compositeScreenProperty),
          hint: notSyncedError ? (
            <span className={styles.warning}>{notSyncedError}</span>
          ) : undefined
        });
      } else {
        fieldsBuilder.addSwitch({
          label: i18n.t('channelGroup.labels.isCompositeCreative'),
          name: 'isCompositeCreative',
          value: isCompositeCreative,
          disabled: true
        });
      }
    }

    fieldsBuilder.addSwitch({
      label: i18n.t('channelGroup.labels.isMultiScreenSameVideo'),
      name: 'isMultiScreenSameVideo',
      value: isMultiScreenSameVideo,
      disabled: true
    });

    // ECOPD-5629: 「開放CBO功能」決定該渠道的活動群組能不能使用 CBO。
    // static 渠道一律唯讀（改成可編輯的話，切了也不會有效果），顯示值分兩種：
    // 內建 CBO 的 RTB / FB 固定 ON，其餘固定 OFF。
    if (isStaticChannel) {
      fieldsBuilder.addSwitch({
        label: i18n.t('channelGroupForm.labels.supportCBO'),
        name: 'addonFeatures.campaign.supportCBO',
        value: isCboBuiltInChannel,
        disabled: true,
        hoverHint: isCboBuiltInChannel
          ? i18n.t('channelGroupForm.labels.supportCBOStaticChannelHint')
          : i18n.t('channelGroupForm.labels.supportCBOUnsupportedChannelHint')
      });
    } else if (!isBeaconChannel) {
      fieldsBuilder.addFormikSwitch({
        label: i18n.t('channelGroupForm.labels.supportCBO'),
        name: 'addonFeatures.campaign.supportCBO'
      });
    }

    !isBeaconChannel &&
      fieldsBuilder.addFormikSwitch({
        label: i18n.t('channelGroupForm.labels.supportPMP'),
        name: 'addonFeatures.campaign.supportPMP'
      });

    const formConfigBuilder = new FormConfig.Builder().addSection(
      new FormConfig.SectionBuilder(fieldsBuilder.build())
        .withTitle(i18n.t('channelGroups.titles.basicContent'))
        .build()
    );
    if (showBrandSection) {
      formConfigBuilder.addSection(
        new FormConfig.SectionBuilder(buildBrandFields())
          .withTitle(i18n.t('channelGroups.titles.brandContent'))
          .build()
      );
    }
    return formConfigBuilder.build();
  }, [
    showBrandSection,
    publisherIds,
    limitPublisherId,
    isBeaconChannel,
    isStaticChannel,
    isCboBuiltInChannel,
    isOutdoorChannel,
    isScreenSpliced,
    isMultiScreenSameVideo,
    screenSplicedVerticalScreenCount,
    screenSplicedHorizontalScreenCount,
    compositeScreenProperty,
    isCompositeCreative,
    compositeSettingStatus,
    values.deliveryUnit,
    values.allowedVideoCodecs,
    isEpaperChannel,
    values.property?.epaperProps?.siteId
  ]);

  return <FormContent formConfig={basicFormConfig} />;
};
